import { useMemo } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'

export function useTraderExternalBankAccounts() {
  const {
    data: rawAccounts,
    error,
    isLoading
  } = trpc.traderExternalBankAccount.list.useQuery(undefined, {
    refetchOnMount: true
  })

  const traderExternalBankAccounts = useMemo(() => rawAccounts ?? [], [rawAccounts])

  return { traderExternalBankAccounts, error, isLoading }
}

export function useTraderExternalBankAccount(id: number, options?: { enabled?: boolean }) {
  const {
    data: raw,
    error,
    isLoading
  } = trpc.traderExternalBankAccount.get.useQuery(
    { id },
    {
      enabled: !!id && options?.enabled !== false
    }
  )

  const traderExternalBankAccount = useMemo(() => raw ?? undefined, [raw])

  return { traderExternalBankAccount, error, isLoading }
}

const invalidateQueries = ['traderExternalBankAccount.list', 'traderExternalBankAccount.get']

export function useTraderExternalBankAccountMutations() {
  const previewMutation = trpc.traderExternalBankAccount.preview.useMutation()

  const commitMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.commit.useMutation,
    createToastConfig('External Bank Account', 'update'),
    { invalidateQueries }
  )

  const deleteMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.delete.useMutation,
    createToastConfig('External Bank Account', 'delete'),
    { invalidateQueries }
  )

  const verifyMicrodepositsMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.verifyMicrodeposits.useMutation,
    createToastConfig('External Bank Account', 'verify'),
    { invalidateQueries }
  )

  const refreshVerificationStatusMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.refreshVerificationStatus.useMutation,
    createToastConfig('External Bank Account', 'update'),
    { invalidateQueries }
  )

  const initiateVerificationMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.initiateVerification.useMutation,
    createToastConfig('External Bank Account', 'create'),
    { invalidateQueries }
  )

  const resetStripeVerificationMutation = useMutationWithToast(
    trpc.traderExternalBankAccount.resetStripeVerification.useMutation,
    createToastConfig('External Bank Account', 'update'),
    { invalidateQueries }
  )

  return {
    previewTraderExternalBankAccount: previewMutation.mutateAsync,
    commitTraderExternalBankAccount: commitMutation.mutateAsync,
    deleteTraderExternalBankAccount: deleteMutation.mutateAsync,
    verifyMicrodeposits: verifyMicrodepositsMutation.mutateAsync,
    refreshVerificationStatus: refreshVerificationStatusMutation.mutateAsync,
    initiateVerification: initiateVerificationMutation.mutateAsync,
    resetStripeVerification: resetStripeVerificationMutation.mutateAsync,
    isPreviewing: previewMutation.isPending,
    isCommitting: commitMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isVerifying: verifyMicrodepositsMutation.isPending,
    isRefreshing: refreshVerificationStatusMutation.isPending,
    isInitiatingVerification: initiateVerificationMutation.isPending,
    isResettingStripeVerification: resetStripeVerificationMutation.isPending
  }
}
